import { FleetAccordionPanel } from '@shared/components/FleetAircraftAccordion';
import { FleetAircraft } from '@shared/components/FleetAircraftList';
import { OperatorDocument, OperatorEmail, OperatorPhone } from '@shared/components/OperatorOverview';
import { AddressRecord, formatAddress } from '@shared/utils/formatAddress';

/** The fields of Core's operator record the overview reads. Both apps call that one endpoint. */
export interface OperatorRecord {
  name: string;
  address?: (AddressRecord & { phone?: string | null; countryCode1?: string | null }) | null;
  phone2?: string | null;
  phone3?: string | null;
  countryCode2?: string | null;
  countryCode3?: string | null;
  phoneType1?: { phoneType?: string | null } | null;
  phoneType2?: { phoneType?: string | null } | null;
  phoneType3?: { phoneType?: string | null } | null;
  preferredPhone1?: boolean | null;
  preferredPhone2?: boolean | null;
  preferredPhone3?: boolean | null;
  email1?: string | null;
  email2?: string | null;
  email3?: string | null;
  emailType1?: { emailType?: string | null } | null;
  emailType2?: { emailType?: string | null } | null;
  emailType3?: { emailType?: string | null } | null;
  preferredEmail1?: boolean | null;
  preferredEmail2?: boolean | null;
  preferredEmail3?: boolean | null;
  baseOfOperation?: { code?: string | null; airportAddress?: { city?: string | null } | null } | null;
  argusRating?: { argusRating?: string | null } | null;
  wyvernRating?: { wyvernRating?: string | null } | null;
  operatorStatus?: boolean | null;
  grade?: string | null;
  operatorNotes?: string | null;
}

export interface OperatorFileRecord {
  hash?: string | null;
  name: string;
  fullUrl?: string | null;
  mimeType?: string | null;
}

export interface OperatorFleetRecord {
  id: number;
  fleetName: string;
  aircraft?: FleetAircraft[] | null;
}

const IMAGE_TYPES = [ 'jpeg', 'jpg', 'png', 'gif' ];

const isImage = (mimeType?: string | null) => IMAGE_TYPES.includes(mimeType?.split('/').pop() ?? '');

// Core numbers the contact slots rather than returning a list: slot 1 lives on the address,
// while 2 and 3 are flat fields carrying a bare country code.
const toPhones = (operator: OperatorRecord): OperatorPhone[] => {
  const phones: OperatorPhone[] = [];
  if (operator.address?.phone) {
    phones.push({
      countryCode: operator.address.countryCode1,
      number: operator.address.phone,
      type: operator.phoneType1?.phoneType,
      preferred: Boolean(operator.preferredPhone1),
    });
  }
  if (operator.phone2) {
    phones.push({
      countryCode: operator.countryCode2 ? `+${operator.countryCode2}` : null,
      number: operator.phone2,
      type: operator.phoneType2?.phoneType,
      preferred: Boolean(operator.preferredPhone2),
    });
  }
  if (operator.phone3) {
    phones.push({
      countryCode: operator.countryCode3 ? `+${operator.countryCode3}` : null,
      number: operator.phone3,
      type: operator.phoneType3?.phoneType,
      preferred: Boolean(operator.preferredPhone3),
    });
  }
  return phones;
};

const toEmails = (operator: OperatorRecord): OperatorEmail[] => {
  const emails: OperatorEmail[] = [];
  if (operator.email1) {
    emails.push({ address: operator.email1, type: operator.emailType1?.emailType, preferred: Boolean(operator.preferredEmail1) });
  }
  if (operator.email2) {
    emails.push({ address: operator.email2, type: operator.emailType2?.emailType, preferred: Boolean(operator.preferredEmail2) });
  }
  if (operator.email3) {
    emails.push({ address: operator.email3, type: operator.emailType3?.emailType, preferred: Boolean(operator.preferredEmail3) });
  }
  return emails;
};

export const toDocuments = (files?: OperatorFileRecord[] | null): OperatorDocument[] =>
  (files ?? [])
    .filter((file) => Boolean(file.fullUrl))
    .map((file, index) => ({
      key: file.hash || String(index),
      name: file.name,
      url: file.fullUrl as string,
      thumbnailUrl: isImage(file.mimeType) ? file.fullUrl : null,
    }));

export const toFleetPanels = (fleets?: OperatorFleetRecord[] | null): FleetAccordionPanel[] =>
  (fleets ?? []).map((fleet) => ({
    key: `fleet-${fleet.id}`,
    title: fleet.fleetName,
    aircraft: fleet.aircraft ?? [],
  }));

export const toOperatorOverviewProps = (operator: OperatorRecord) => ({
  name: operator.name,
  phones: toPhones(operator),
  emails: toEmails(operator),
  address: formatAddress(operator.address),
  baseOfOperation: operator.baseOfOperation
    ? [ operator.baseOfOperation.code, operator.baseOfOperation.airportAddress?.city ].filter(Boolean).join(', ')
    : '',
  argusRating: operator.argusRating?.argusRating ?? undefined,
  wyvernRating: operator.wyvernRating?.wyvernRating ?? undefined,
  approved: !operator.operatorStatus,
  grade: operator.grade ?? undefined,
  notes: operator.operatorNotes ?? undefined,
});
